import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { SIGNALS } from '@/constants/testIds';
import { toast } from 'sonner';
import { AlertTriangle, ExternalLink, MessageSquare, Newspaper, RefreshCw, Rss, Trash2, TrendingUp } from 'lucide-react';

const SOURCE_META = {
  hn:     { label: 'HN',     color: '#FF6600', icon: TrendingUp },
  reddit: { label: 'REDDIT', color: '#FF4500', icon: Newspaper },
  rss:    { label: 'RSS',    color: '#38BDF8', icon: Rss },
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return iso;
  }
};

export const SignalsPage = () => {
  const { user } = useAuth();
  const isPro = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';

  const [queries, setQueries] = useState([]);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyAdd, setBusyAdd] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [label, setLabel] = useState('');
  const [rssEnabled, setRssEnabled] = useState(false);
  const [rssUrl, setRssUrl] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [queryFilter, setQueryFilter] = useState('all');

  const load = useCallback(async () => {
    if (!isPro) { setLoading(false); return; }
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        api.get('/signals/queries'),
        api.get('/signals', { params: { limit: 100 } }),
      ]);
      setQueries(q.data);
      setSignals(s.data);
    } catch {
      toast.error('Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!keywords.trim()) return;
    if (rssEnabled && !rssUrl.trim()) {
      toast.error('RSS_URL_REQUIRED');
      return;
    }
    setBusyAdd(true);
    try {
      const sources = ['hn', 'reddit'];
      if (rssEnabled) sources.push('rss');
      await api.post('/signals/queries', {
        keywords: keywords.trim(),
        label: label.trim() || null,
        sources,
        rss_url: rssEnabled ? rssUrl.trim() : null,
      });
      setKeywords(''); setLabel(''); setRssUrl(''); setRssEnabled(false);
      toast.success('QUERY_SAVED · SCANNING');
      setTimeout(load, 2500);
      setTimeout(load, 6000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Add failed');
    } finally {
      setBusyAdd(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/signals/queries/${id}`);
      toast.success('QUERY_REMOVED');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const rescan = async (id) => {
    try {
      await api.post(`/signals/queries/${id}/scan`);
      toast.success('RE_SCANNED');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    }
  };

  const markSeen = async () => {
    try {
      await api.post('/signals/mark-seen');
      load();
    } catch { toast.error('Mark-seen failed'); }
  };

  const filtered = useMemo(() => {
    return signals.filter((s) =>
      (sourceFilter === 'all' || s.source === sourceFilter) &&
      (queryFilter === 'all' || s.query_id === queryFilter)
    );
  }, [signals, sourceFilter, queryFilter]);

  const unseen = signals.filter((s) => !s.seen).length;

  if (!isPro) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-8 pt-16 pb-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">// ACCESS_GATE</div>
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
          Live Signal Feed<span className="caelix-caret"></span>
        </h1>
        <div data-testid={SIGNALS.upgradeGate} className="c-card">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" strokeWidth={2.5} />
            <div>
              <div className="text-white text-sm uppercase tracking-widest">PRO_TIER_REQUIRED</div>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Save keyword queries. Caelix streams matching stories from HackerNews and
                Reddit into your feed every 5 minutes. Zero polling, terminal-native triage.
              </p>
            </div>
          </div>
          <Link to="/pricing" className="c-btn-primary text-xs">UPGRADE_CLEARANCE</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={SIGNALS.container} className="max-w-6xl mx-auto px-6 md:px-8 pt-10 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">// LIVE_SIGNAL_FEED</div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
        Signals<span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 mb-6 max-w-2xl">
        &gt; Save keyword queries; Caelix scrapes HackerNews + Reddit + your custom RSS feeds every 5 minutes and streams matches here. Up to 50 signals retained per query.
      </p>

      {/* Add form */}
      <form data-testid={SIGNALS.addForm} onSubmit={add} className="c-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="c-label" htmlFor="sig-kw">KEYWORDS</label>
            <input
              id="sig-kw"
              data-testid={SIGNALS.addKeywords}
              type="text"
              required
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder='e.g. "supply chain attack" OR "npm compromise"'
              className="c-input"
            />
          </div>
          <div>
            <label className="c-label" htmlFor="sig-label">LABEL (OPTIONAL)</label>
            <input
              id="sig-label"
              data-testid={SIGNALS.addLabel}
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="daily triage"
              maxLength={64}
              className="c-input"
            />
          </div>
          <button
            data-testid={SIGNALS.addSubmit}
            type="submit"
            disabled={busyAdd}
            className="c-btn-primary text-xs h-[42px]"
          >
            {busyAdd ? 'SAVING…' : 'SAVE_QUERY'}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-[#3F3F46]">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid={SIGNALS.addRssToggle}
              type="checkbox"
              checked={rssEnabled}
              onChange={(e) => setRssEnabled(e.target.checked)}
              className="accent-[#38BDF8]"
            />
            <Rss className="w-3 h-3 text-[#38BDF8]" strokeWidth={2.5} />
            <span className="text-[10px] tracking-[0.2em] uppercase text-gray-400">
              Include a custom RSS/Atom feed
            </span>
          </label>
          {rssEnabled && (
            <input
              data-testid={SIGNALS.addRssUrl}
              type="url"
              value={rssUrl}
              onChange={(e) => setRssUrl(e.target.value)}
              placeholder="https://feeds.example.com/rss.xml"
              className="c-input mt-3"
              required
            />
          )}
        </div>
      </form>

      {/* Saved queries strip */}
      {queries.length > 0 && (
        <div className="c-card mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 border-b border-[#3F3F46] pb-3 mb-4">
            // SAVED_QUERIES · {queries.length}/15
          </div>
          <div className="grid grid-cols-1 gap-2">
            {queries.map((q) => (
              <div
                key={q.id}
                data-testid={SIGNALS.queryRow}
                className="grid grid-cols-[1.5fr_1fr_100px_120px_90px] gap-3 items-center py-2 border-b border-[#27272A] last:border-0 text-xs"
              >
                <div className="font-mono text-white truncate">{q.keywords}</div>
                <div className="text-gray-500 font-mono truncate">{q.label || '—'}</div>
                <div className="font-mono text-[#38BDF8]">
                  {q.signals_count} SIG {q.unseen_signals > 0 && (
                    <span className="c-badge c-badge--warn ml-1">{q.unseen_signals} NEW</span>
                  )}
                </div>
                <div className="text-gray-500 text-[10px] font-mono">{timeAgo(q.last_scanned_at)}</div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    data-testid={SIGNALS.queryScan}
                    onClick={() => rescan(q.id)}
                    className="c-btn-secondary text-[10px] px-2 py-1"
                    title="Rescan"
                  >
                    <RefreshCw className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                  <button
                    data-testid={SIGNALS.queryDelete}
                    onClick={() => remove(q.id)}
                    className="c-btn-danger text-[10px] px-2 py-1"
                    title="Remove"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="c-card mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <FilterChip
              testId={SIGNALS.filterAll}
              active={sourceFilter === 'all'}
              onClick={() => setSourceFilter('all')}
            >ALL</FilterChip>
            <FilterChip
              testId={SIGNALS.filterHn}
              active={sourceFilter === 'hn'}
              onClick={() => setSourceFilter('hn')}
            >HN</FilterChip>
            <FilterChip
              testId={SIGNALS.filterReddit}
              active={sourceFilter === 'reddit'}
              onClick={() => setSourceFilter('reddit')}
            >REDDIT</FilterChip>
            <FilterChip
              testId={SIGNALS.filterRss}
              active={sourceFilter === 'rss'}
              onClick={() => setSourceFilter('rss')}
            >RSS</FilterChip>
            {queries.length > 0 && (
              <select
                data-testid={SIGNALS.filterQuery}
                value={queryFilter}
                onChange={(e) => setQueryFilter(e.target.value)}
                className="c-input py-1 h-auto text-[10px] w-auto"
              >
                <option value="all">ALL_QUERIES</option>
                {queries.map((q) => (
                  <option key={q.id} value={q.id}>{q.keywords.slice(0, 40)}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className={`c-badge ${unseen > 0 ? 'c-badge--warn' : ''}`}>{unseen} UNSEEN</span>
            <button
              data-testid={SIGNALS.markSeenBtn}
              onClick={markSeen}
              disabled={unseen === 0}
              className="c-btn-secondary text-[10px] px-3 py-1.5"
            >
              MARK_ALL_SEEN
            </button>
          </div>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="c-card text-xs text-gray-500 tracking-widest uppercase">
          LOADING<span className="caelix-dots"></span>
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid={SIGNALS.emptyState} className="c-card">
          <div className="text-xs text-gray-500 tracking-widest uppercase">
            {queries.length === 0
              ? '> save a query above to start receiving signals'
              : '> no signals match your filters yet · check back in a few minutes'}
          </div>
        </div>
      ) : (
        <div className="c-card">
          <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 border-b border-[#3F3F46] pb-3 mb-4">
            // FEED · {filtered.length} SHOWING
          </div>
          <div className="space-y-2">
            {filtered.map((s) => {
              const sm = SOURCE_META[s.source] || SOURCE_META.hn;
              const SIcon = sm.icon;
              return (
                <a
                  key={s.id}
                  data-testid={SIGNALS.signalItem}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block border ${s.seen ? 'border-[#27272A] opacity-60' : 'border-[#3F3F46]'} bg-black p-3 hover:border-[#38BDF8]/50 transition-colors group`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <SIcon className="w-3 h-3" strokeWidth={2.5} style={{ color: sm.color }} />
                        <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: sm.color }}>
                          {sm.label}
                        </span>
                        {s.author && (
                          <span className="text-[10px] tracking-widest uppercase text-gray-600">
                            · {s.author}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white group-hover:text-[#38BDF8] transition-colors truncate">
                        {s.title}
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-[10px] tracking-widest uppercase text-gray-600 font-mono">
                        {typeof s.score === 'number' && (
                          <span className="inline-flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> {s.score}
                          </span>
                        )}
                        {typeof s.comments === 'number' && (
                          <span className="inline-flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" strokeWidth={2.5} /> {s.comments}
                          </span>
                        )}
                        <span>{timeAgo(s.posted_at || s.ingested_at)}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#38BDF8] transition-colors shrink-0" strokeWidth={2.5} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterChip = ({ active, onClick, testId, children }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border transition-colors ${
      active
        ? 'border-[#38BDF8] text-[#38BDF8] bg-[#38BDF8]/[0.06]'
        : 'border-[#3F3F46] text-gray-500 hover:border-[#38BDF8]/40 hover:text-white'
    }`}
  >
    {children}
  </button>
);

export default SignalsPage;
